import {
  AdditiveBlending,
  Color,
  DoubleSide,
  DynamicDrawUsage,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  MeshBasicNodeMaterial,
  NormalBlending,
  PlaneGeometry,
  Quaternion,
  Vector3,
  type Texture,
} from 'three/webgpu';
import { abs, float, instancedBufferAttribute, length, max, mix, oneMinus, smoothstep, texture, uv, vec2, vec4 } from 'three/tsl';
import type { FrameInfo } from '@src/game/AnimDefs';

export enum ShapeKind {
  Circle = 0,
  Ring,
  Bolt,
  Diamond,
  Square,
  Wisp,
  Shadow,
  /** Vortex gate: rune rim, spiral arms, dark mouth. Distinct from Mine's thin Ring. */
  Portal,
}

export interface SpritePoolOptions {
  capacity: number;
  additive: boolean;
  renderOrder: number;
  shape: ShapeKind;
  /** Frame table for this sprite; null (or no atlas) uses the procedural shape. */
  frames: readonly FrameInfo[] | null;
  atlas: Texture | null;
  /**
   * Painter's order within the pool: instances are written top-of-screen first
   * (larger world y) so a sprite lower on screen overdraws the one behind it.
   * Needed because the pool draws with depthTest off, where instance index is
   * the only thing that decides overlap. Costs a staging copy plus one sort per frame.
   */
  ySort?: boolean;
}

/** Per-instance staging floats for y-sorted pools: x y z rot sx sy tint alpha frame flash key. */
const STAGE_STRIDE = 11;
const STAGE_KEY = 10;

const tmpMat = new Matrix4();
const tmpPos = new Vector3();
const tmpQuat = new Quaternion();
const tmpScale = new Vector3();
const tmpColor = new Color();
const Z_AXIS = new Vector3(0, 0, 1);

function proceduralMask(shape: ShapeKind) {
  const p = uv().sub(0.5).mul(2); // -1..1
  const d = length(p);
  switch (shape) {
    case ShapeKind.Ring: {
      const ring = smoothstep(0.32, 0.12, abs(d.sub(0.72)));
      const core = smoothstep(0.6, 0.0, d).mul(0.35);
      return max(ring, core);
    }
    case ShapeKind.Portal: {
      const ring = smoothstep(0.16, 0.05, abs(d.sub(0.7)));
      const rim = smoothstep(0.22, 0.08, abs(d.sub(0.86))).mul(0.55);
      const arms = oneMinus(smoothstep(0.18, 0.55, abs(p.x.mul(p.y).mul(6)))).mul(smoothstep(0.82, 0.2, d)).mul(0.45);
      const mouth = smoothstep(0.55, 0.12, d).mul(0.4);
      const hole = smoothstep(0.2, 0.06, d);
      return max(max(ring, rim), max(arms, mouth)).mul(oneMinus(hole.mul(0.85)));
    }
    case ShapeKind.Bolt: {
      const ax = abs(p.x);
      const ay = abs(p.y);
      const body = oneMinus(smoothstep(0.55, 1.0, ax)).mul(oneMinus(smoothstep(0.12, 0.42, ay)));
      const glow = oneMinus(smoothstep(0.0, 1.0, length(vec2(p.x.mul(0.6), p.y.mul(1.6))))).mul(0.6);
      return max(body, glow);
    }
    case ShapeKind.Diamond: {
      const m = abs(p.x).add(abs(p.y));
      return smoothstep(0.95, 0.75, m);
    }
    case ShapeKind.Square: {
      const m = max(abs(p.x), abs(p.y));
      return smoothstep(0.9, 0.78, m);
    }
    case ShapeKind.Wisp: {
      const body = smoothstep(1.0, 0.35, d);
      const tail = smoothstep(0.7, 0.2, abs(p.x)).mul(smoothstep(-1.0, -0.2, p.y)).mul(0.5);
      return max(body.mul(0.9), tail);
    }
    case ShapeKind.Shadow:
      return smoothstep(1.0, 0.3, d).mul(0.55);
    case ShapeKind.Circle:
    default: {
      const disc = smoothstep(1.0, 0.82, d);
      const core = smoothstep(0.6, 0.0, d).mul(0.5);
      return mix(disc, float(1), core);
    }
  }
}

/**
 * One InstancedMesh per sprite archetype. Each instance carries its own atlas UV
 * rect so animation frames are a per-instance attribute; tint and alpha ride along.
 */
export class SpritePool {
  readonly mesh: InstancedMesh;
  readonly capacity: number;
  readonly hasAtlasFrame: boolean;
  readonly frames: readonly FrameInfo[];
  private readonly alpha: InstancedBufferAttribute;
  private readonly flash: InstancedBufferAttribute;
  private readonly uvRect: InstancedBufferAttribute;
  private readonly staged: Float32Array | null;
  private readonly order: Uint32Array | null;
  private count = 0;

  constructor(opts: SpritePoolOptions) {
    this.capacity = opts.capacity;
    this.hasAtlasFrame = Boolean(opts.frames && opts.atlas);
    this.frames = opts.frames ?? [];
    this.staged = opts.ySort ? new Float32Array(opts.capacity * STAGE_STRIDE) : null;
    this.order = opts.ySort ? new Uint32Array(opts.capacity) : null;
    const geometry = new PlaneGeometry(1, 1);

    this.alpha = new InstancedBufferAttribute(new Float32Array(opts.capacity).fill(1), 1);
    this.alpha.setUsage(DynamicDrawUsage);
    this.flash = new InstancedBufferAttribute(new Float32Array(opts.capacity), 1);
    this.flash.setUsage(DynamicDrawUsage);
    const rects = new Float32Array(opts.capacity * 4);
    for (let i = 0; i < opts.capacity; i++) {
      rects[i * 4 + 2] = 1;
      rects[i * 4 + 3] = 1;
    }
    this.uvRect = new InstancedBufferAttribute(rects, 4);
    this.uvRect.setUsage(DynamicDrawUsage);

    const material = new MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthWrite = false;
    material.depthTest = false;
    material.side = DoubleSide;
    material.blending = opts.additive ? AdditiveBlending : NormalBlending;
    const alphaNode = instancedBufferAttribute<'float'>(this.alpha, 'float');
    const flashNode = instancedBufferAttribute<'float'>(this.flash, 'float');
    if (this.hasAtlasFrame && opts.atlas) {
      const rect = instancedBufferAttribute<'vec4'>(this.uvRect, 'vec4');
      // lerp(rect.xy, rect.zw, uv) written out: the TSL mix() typings reject vec2 nodes.
      const frameUv = rect.xy.add(rect.zw.sub(rect.xy).mul(uv()));
      const tex = texture(opts.atlas, frameUv);
      // Hit flash: push the texel toward white; the instance tint still multiplies after.
      // Squared so the (linear-space) blend fades perceptually instead of staying washed out.
      const lit = tex.rgb.add(oneMinus(tex.rgb).mul(flashNode.mul(flashNode)));
      material.colorNode = vec4(lit, float(1));
      material.opacityNode = tex.a.mul(alphaNode);
    } else {
      material.colorNode = vec4(1, 1, 1, 1);
      material.opacityNode = proceduralMask(opts.shape).mul(alphaNode);
    }

    this.mesh = new InstancedMesh(geometry, material, opts.capacity);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = opts.renderOrder;
    this.mesh.count = 0;
    // Allocate instanceColor up front so the material compiles with tinting enabled.
    tmpColor.setHex(0xffffff);
    for (let i = 0; i < opts.capacity; i++) this.mesh.setColorAt(i, tmpColor);
  }

  begin(): void {
    this.count = 0;
  }

  /**
   * Writes one instance. `sx`/`sy` are the quad's world size (sign of sx flips).
   * `flash` (0..1) whites out atlas texels for hit feedback. Returns false when the pool is full.
   * `sortY` is the depth key for y-sorted pools (defaults to `y`; pass the ground
   * position so bob and hover offsets do not shuffle the order every frame).
   */
  push(x: number, y: number, z: number, rot: number, sx: number, sy: number, tint: number, alpha: number, frameIndex: number, flash = 0, sortY = y): boolean {
    if (this.count >= this.capacity) return false;
    const i = this.count++;
    const s = this.staged;
    if (s) {
      const o = i * STAGE_STRIDE;
      s[o] = x;
      s[o + 1] = y;
      s[o + 2] = z;
      s[o + 3] = rot;
      s[o + 4] = sx;
      s[o + 5] = sy;
      s[o + 6] = tint;
      s[o + 7] = alpha;
      s[o + 8] = frameIndex;
      s[o + 9] = flash;
      s[o + STAGE_KEY] = sortY;
      return true;
    }
    this.write(i, x, y, z, rot, sx, sy, tint, alpha, frameIndex, flash);
    return true;
  }

  private write(i: number, x: number, y: number, z: number, rot: number, sx: number, sy: number, tint: number, alpha: number, frameIndex: number, flash: number): void {
    tmpPos.set(x, y, z);
    tmpQuat.setFromAxisAngle(Z_AXIS, rot);
    tmpScale.set(sx, sy, 1);
    tmpMat.compose(tmpPos, tmpQuat, tmpScale);
    this.mesh.setMatrixAt(i, tmpMat);
    tmpColor.setHex(tint);
    this.mesh.setColorAt(i, tmpColor);
    this.alpha.array[i] = alpha;
    this.flash.array[i] = flash;
    if (this.hasAtlasFrame) {
      const f = this.frames[frameIndex] ?? this.frames[0];
      const o = i * 4;
      const r = this.uvRect.array as Float32Array;
      r[o] = f.u0;
      r[o + 1] = f.v0;
      r[o + 2] = f.u1;
      r[o + 3] = f.v1;
    }
  }

  /** Top of screen first: larger world y is further from the viewer under the top-down camera. */
  private flushSorted(): void {
    const s = this.staged as Float32Array;
    const n = this.count;
    const order = (this.order as Uint32Array).subarray(0, n);
    for (let i = 0; i < n; i++) order[i] = i;
    order.sort((a, b) => s[b * STAGE_STRIDE + STAGE_KEY] - s[a * STAGE_STRIDE + STAGE_KEY]);
    for (let i = 0; i < n; i++) {
      const o = order[i] * STAGE_STRIDE;
      this.write(i, s[o], s[o + 1], s[o + 2], s[o + 3], s[o + 4], s[o + 5], s[o + 6], s[o + 7], s[o + 8], s[o + 9]);
    }
  }

  end(): void {
    this.mesh.count = this.count;
    if (this.count === 0) return;
    if (this.staged) this.flushSorted();
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    this.alpha.needsUpdate = true;
    this.flash.needsUpdate = true;
    if (this.hasAtlasFrame) this.uvRect.needsUpdate = true;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as MeshBasicNodeMaterial).dispose();
  }
}
